/**
 * ApifyUsageWidget — live Apify spend + projection card for the Dashboard.
 *
 * Fetches GET /api/admin/apify-usage every 5 minutes (admin-token-gated, so
 * silently returns null for non-admin viewers). Renders a horizontal card:
 *   • Big spend number with $limit context + colour-coded progress bar
 *   • Days elapsed / days remaining in current cycle
 *   • Daily burn rate
 *   • Projected end-of-cycle total (red-flagged if >limit)
 *   • Subtle "data via Apify API" footer
 *
 * Tone colours:
 *   ok     (<70% used, projected ≤ limit)      → emerald
 *   warn   (70-90% used or projected > limit)  → amber
 *   danger (>=90% used or projected > 1.1×)    → rose
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "pp_admin_token";

const TONE_THEME = {
  ok: {
    ring: "ring-emerald-200",
    bar: "bg-emerald-500",
    bigText: "text-emerald-700",
    chipBg: "bg-emerald-50 text-emerald-700 border-emerald-200",
    eyebrow: "text-emerald-600",
    bgGradient: "from-emerald-50 via-white to-white",
  },
  warn: {
    ring: "ring-amber-200",
    bar: "bg-amber-500",
    bigText: "text-amber-800",
    chipBg: "bg-amber-50 text-amber-800 border-amber-200",
    eyebrow: "text-amber-700",
    bgGradient: "from-amber-50 via-white to-white",
  },
  danger: {
    ring: "ring-rose-300",
    bar: "bg-rose-500",
    bigText: "text-rose-700",
    chipBg: "bg-rose-50 text-rose-700 border-rose-200",
    eyebrow: "text-rose-600",
    bgGradient: "from-rose-50 via-white to-white",
  },
};

const ApifyUsageWidget = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchUsage = useCallback(async () => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!adminToken) {
      // Not an admin viewer — silently no-op so the widget hides itself.
      setError("no-admin");
      return;
    }
    try {
      const r = await axios.get(`${API}/admin/apify-usage`, {
        headers: { "x-admin-token": adminToken },
        timeout: 10000,
      });
      setData(r.data);
      setError(null);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        // Token expired — hide the widget rather than scream at the user.
        setError("expired");
      } else {
        setError(e?.response?.data?.detail || e?.message || "Failed to fetch usage");
      }
      setData(null);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
    const id = setInterval(fetchUsage, 5 * 60 * 1000); // 5 min
    return () => clearInterval(id);
  }, [fetchUsage]);

  // Hide entirely for non-admin viewers (most common case) or expired tokens.
  if (error === "no-admin" || error === "expired") return null;
  if (!data && !error) return null; // still loading on first paint

  if (error && !data) {
    return (
      <div className="rounded-2xl ring-1 ring-zinc-200 bg-white px-5 py-3 text-[12px] text-zinc-500" data-testid="apify-usage-widget-error">
        Apify usage unavailable — {error}
      </div>
    );
  }

  const theme = TONE_THEME[data.tone] || TONE_THEME.ok;
  const pctWidth = Math.min(100, Math.max(2, data.pct_used)).toFixed(1) + "%";
  const overProjected = data.projected_usd > data.limit_usd;

  return (
    <div
      className={`rounded-2xl ring-1 ${theme.ring} bg-gradient-to-r ${theme.bgGradient} px-5 sm:px-6 py-4 sm:py-5`}
      data-testid="apify-usage-widget"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className={`text-[10px] font-mono-data uppercase tracking-[0.2em] font-bold ${theme.eyebrow}`}>
            Apify spend · current cycle
          </div>
          <div className="mt-1 flex items-baseline gap-2 flex-wrap">
            <span className={`text-3xl sm:text-4xl font-bold ${theme.bigText} font-mono-data`} data-testid="apify-usage-current">
              ${data.used_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-zinc-500 text-sm">/ ${data.limit_usd.toLocaleString()} limit</span>
            <span className={`text-[10px] font-mono-data uppercase tracking-wider border px-1.5 py-0.5 rounded ${theme.chipBg}`}>
              {data.pct_used.toFixed(1)}%
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500">Cycle</div>
          <div className="text-[12px] font-mono-data text-zinc-700 mt-0.5">
            Day {data.days_elapsed} / {data.days_total}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">{data.days_remaining}d remaining</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 w-full rounded-full bg-zinc-100 overflow-hidden mb-3">
        <div
          className={`h-full ${theme.bar} transition-all`}
          style={{ width: pctWidth }}
          data-testid="apify-usage-bar"
        />
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[12px]">
        <div>
          <div className="text-[9.5px] font-mono-data uppercase tracking-[0.2em] text-zinc-500">Daily burn</div>
          <div className="text-zinc-900 font-semibold font-mono-data mt-0.5" data-testid="apify-usage-burn">
            ${data.daily_burn_usd.toFixed(2)}/day
          </div>
        </div>
        <div>
          <div className="text-[9.5px] font-mono-data uppercase tracking-[0.2em] text-zinc-500">Projected total</div>
          <div className={`font-semibold font-mono-data mt-0.5 ${overProjected ? theme.bigText : "text-zinc-900"}`}
               data-testid="apify-usage-projected">
            ${data.projected_usd.toFixed(2)}
            {overProjected && <span className="ml-1 text-[10px]">⚠ over limit</span>}
          </div>
        </div>
        <div>
          <div className="text-[9.5px] font-mono-data uppercase tracking-[0.2em] text-zinc-500">Compute units</div>
          <div className="text-zinc-900 font-semibold font-mono-data mt-0.5">
            {data.compute_units_used.toFixed(1)}
            <span className="text-zinc-400 font-normal"> / {(data.compute_units_limit / 1000).toFixed(0)}k</span>
          </div>
        </div>
      </div>

      <div className="mt-3 text-[10px] text-zinc-400 font-mono-data">
        Live data via Apify API · refreshes every 5 min
      </div>
    </div>
  );
};

export default ApifyUsageWidget;
